import NetxQueryClient, {
	SuccessResponse,
} from '@netx/core-next/lib/queryClient';
export type {QueryError} from '@netx/core-next/lib/queryClient';
import {USER_DEFAULTS} from '@netx/core-user/constants';
import type {UserProfile, NewUserProfile} from '@netx/core-user/types';
import {Optional, Required} from 'utility-types';
import {authRequireEmailAsLogin} from '@netx/core-auth-next/enablers';

/**
 * `userSelfRegistration` request params
 * (what is used to send to server)
 */
export interface UserSelfRegistrationRequestOptions
	extends Required<
		Optional<NewUserProfile>,
		'email' | 'login' | 'name1' | 'name3' | 'password1' | 'password2'
	> {
	autogeneratePassword?: boolean;
}

/**
 * `userSelfRegistration` request
 * (what gets sent to the server)
 */
export type UserSelfRegistrationRequest = [NewUserProfile];

/**
 * `userSelfRegistration` response
 * (what comes back from the server)
 */
export type UserSelfRegistrationResponse = SuccessResponse<void>;

/**
 * `userSelfRegistration` result
 * (what is utilized from the server response)
 */
export type UserSelfRegistrationResult = UserProfile;

export const userSelfRegistrationEndpoint = 'userSelfRegistration';

/**
 * (self)Register user
 * @param fetchParams Fetch params
 * @returns Session key
 */
export const userSelfRegistration = async ({
	autogeneratePassword,
	email,
	login,
	name1,
	name3,
	password1,
	password2,
	...rest
}: UserSelfRegistrationRequestOptions): Promise<UserProfile> => {
	const client = NetxQueryClient();

	const userProfile = {
		...USER_DEFAULTS,
		...rest,
		email,
		login: authRequireEmailAsLogin() ? email : login,
		name1,
		name3,
		password1: autogeneratePassword ? '' : password1,
		password2: autogeneratePassword ? '' : password2,
	};

	const requestParams: UserSelfRegistrationRequest = [userProfile];

	return client
		.post<UserSelfRegistrationResponse>(
			userSelfRegistrationEndpoint,
			requestParams,
		)
		.then((response) => ({...userProfile, userId: -1}));
};

export default userSelfRegistration;
